import { Observable, Subscription, filter } from 'rxjs';
import { HttpContext } from '@angular/common/http';
import { quietRead, QUIET_HTTP_ERRORS } from 'src/app/common/background-request';
import { businessHours, businessStatus, tooltipTriggers } from 'src/app/common/function';
import { directionsUrl } from 'src/app/common/directions';
import { restaurantImageUrl } from 'src/app/common/restaurant-image-url';
import { isInListingViewport, listingQueryKey, listingViewport, ListingLoadResult } from 'src/app/common/listing-viewport';
import { BusinessFilter } from 'src/app/models/register-business';
import { state, style, transition, animate, trigger } from '@angular/animations';
import { BusinessService } from 'src/app/services/business.service';
import { ToastrService } from 'ngx-toastr';
import { LoaderService } from './../../../services/loader.service';
import { RestaurantListFilter } from './../../../models/restaurant-details';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnDestroy,
  OnChanges,
  SimpleChanges
} from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { RestaurantDetailModalComponent } from 'src/app/components/restaurant-detail-modal/restaurant-detail-modal.component';
import {
  RestaurantDetails,
  RestaurantListType,
} from 'src/app/models/restaurant-details';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { hasNativeLoginBridge } from 'src/app/shared/auth/auth-runtime-context';
import { takeUntil, debounceTime, finalize, timeout, map } from 'rxjs/operators';
import { TourService } from 'src/app/tour/tour.service';

interface CardImageState {
  key: string;
  url: string;
  stage: 'profile' | 'original' | 'placeholder';
  pending: boolean;
  subscription?: Subscription;
}

/** Absolute listing indices (1-based, inclusive) whose pins the map should show. */
export interface PinWindow {
  start: number;
  end: number;
}

@Component({
  selector: 'app-restaurant-detail-section',
  templateUrl: './restaurant-detail-section.component.html',
  styleUrls: ['./restaurant-detail-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [trigger('fadeIn', [
    state('hidden', style({ opacity: 0 })),
    state('visible', style({ opacity: 1 })),
    transition('hidden => visible', animate('100ms ease-in')),
  ])]
})
export class RestaurantDetailSectionComponent implements OnInit, OnDestroy, OnChanges {
  private destroy$ = new Subject<void>();
  private destroyed = false;
  private recoveryAfter = 0;
  private loadDataSubject = new Subject<void>();
  private tourListing = false;
  @Input() center: any;
  @Input() gridView: boolean = false;
  @Input() restaurantListType: RestaurantListType;
  @Input()
  set businessFilter(value) {
    if (value) {
      if (listingQueryKey(value) !== listingQueryKey(this._businessFilter)) {
        // Invalidate immediately, before the debounced replacement request starts.
        ++this.requestId;
        this.listingRequest?.unsubscribe();
        this.isLoading = false;
      }
      this._businessFilter = value;
      if (!this.lastListEpicenter) {
        this.lastListEpicenter = this.getFilterEpicenter(this._businessFilter);
      }
      if (this.restoredFromSession && this.restoredFilterKey) {
        const incomingKey = this.buildFilterKey(this._businessFilter, this.showOpen, this.showClose);
        if (incomingKey === this.restoredFilterKey) {
          return;
        }
        this.restoredFromSession = false;
      }
      if (!this.tour.active && !this.triedRestoreFromSession && (!this.businessService.filterDataObject || this.businessService.hasListingReturnPending())) {
        this.triedRestoreFromSession = true;
        const storedState = this.businessService.hydrateListingState();
        const storedFilterData = storedState?.filterDataObject;
        const storedMeta = storedState?.listMeta;
        const hasStoredList = Array.isArray(storedState?.restaurantDetailsList);
        if (hasStoredList && this.businessService.shouldRestoreListingState(storedState)) {
          const storedShowOpen = typeof storedFilterData?.showOpen === 'boolean'
            ? storedFilterData.showOpen
            : storedMeta?.showOpen;
          const storedShowClose = typeof storedFilterData?.showClose === 'boolean'
            ? storedFilterData.showClose
            : storedMeta?.showClose;
          const storedKey = this.buildFilterKey(storedFilterData?.filter, storedShowOpen, storedShowClose);
          const incomingKey = this.buildFilterKey(this._businessFilter, storedShowOpen, storedShowClose);
          if (storedKey && storedKey === incomingKey) {
            if (this.restoreListingFromSessionState()) {
              return;
            }
          }
        }
      }
      if (this.ignoreNextFilterReset) {
        const incomingKey = this.buildFilterKey(this._businessFilter, this.showOpen, this.showClose);
        this.ignoreNextFilterReset = false;
        if (this.restoredFilterKey && this.restoredFilterKey === incomingKey) {
          return;
        }
      }
      if (this.businessFilter.zoomLevel >= this.restaurantDetailsList[0]?.zoomLevel) {
        this.restaurantDetailsList = [];
        this.restaurantCache.clear();
      }
      this.currentPage = 0;
      this.requestedPage = undefined;
      this.hasLoadedPage = false;
      if (this.restaurantListType) {
        this.loadDataSubject.next();
      }
    }
  }
  get businessFilter(): BusinessFilter {
    return this._businessFilter;
  }

  @Output() onListLoad = new EventEmitter<any>();
  @Output() loadFailed = new EventEmitter<void>();
  @Output() loadCompleted = new EventEmitter<ListingLoadResult>();
  @Output() pinWindowChange = new EventEmitter<PinWindow | null>();

  @ViewChildren('restaurantListItems', { read: ElementRef })
  restaurantListItems!: QueryList<ElementRef>;
  @ViewChildren('pagingSentinel', { read: ElementRef })
  pagingSentinels!: QueryList<ElementRef<HTMLElement>>;

  @ViewChild(RestaurantDetailModalComponent)
  restaurantDetailModalComponent!: RestaurantDetailModalComponent;
  @ViewChild('listScrollContainer')
  listScrollContainer?: ElementRef<HTMLElement>;

  restaurantList: RestaurantDetails[] = [];
  restaurantDetailsList: RestaurantDetails[] = [];
  item: RestaurantDetails = new RestaurantDetails();
  modalViewActive: boolean = false;
  modalRef: NgbModalRef | null = null;
  currentPage = 0;
  totalPage = 1;
  resturantCoordinates = [];
  _businessFilter = new BusinessFilter();
  showOpen: boolean = false;
  showClose: boolean = false;
  limit = 30;
  totalRecords: number = 100;
  visibleChipCount = 3;
  hoursTooltipTriggers = tooltipTriggers();
  isFromMobileApp: boolean = false;
  private intersectionObserver: IntersectionObserver;
  @Input() searchIds: any[] = [];
  searchRestaurantList: any;
  isFromDashboard: boolean = false;
  @Input() isCall: boolean = false;
  private requestId = 0;
  private listingRequest?: Subscription;
  isLoading = false;
  loadError = '';
  private pendingSearchIdsFilter = false;
  private skipNextCenterLoad = false;
  private skipNextLoad = false;
  private ignoreNextFilterReset = false;
  private restoredFilterKey: string | null = null;
  private triedRestoreFromSession = false;
  private restoredFromSession = false;
  private pendingScrollTop: number | null = null;
  private lastListEpicenter: { lat: number; lng: number } | null = null;
  private pendingEpicenterRefresh = false;
  private restaurantCache = new Map<number, RestaurantDetails>();
  private readonly maxCachedRestaurants = 160;
  private visiblePages = new Map<number, RestaurantDetails[]>();
  private requestedPage?: number;
  private hasLoadedPage = false;
  private readonly maxVisiblePages = 5;
  get firstVisiblePage(): number { return this.visiblePages.size ? Math.min(...this.visiblePages.keys()) : 0; }
  get hasMoreBusinesses(): boolean {
    return this.isCall && this.hasLoadedPage && !this.businessFilter.search?.trim() && !this.searchIds?.length && this.currentPage + 1 < this.totalPage;
  }
  get hasEarlierBusinesses(): boolean {
    return this.isCall && this.hasLoadedPage && !this.businessFilter.search?.trim() && !this.searchIds?.length && this.firstVisiblePage > 0;
  }
  /** Which list end is waiting for an adjacent batch, for its inline spinner. */
  get pagingDirection(): 'next' | 'previous' | null {
    if (!this.isLoading || this.requestedPage == null) return null;
    return this.requestedPage > this.currentPage ? 'next' : 'previous';
  }
  private readonly imagePlaceholder = './assets/images/no-image.svg';
  private cardImages = new Map<number, CardImageState>();
  private visibleImageIds = new Set<number>();
  private readonly maxCardImages = 60;
  private readonly maxCardImageBytes = 12 * 1024 * 1024;
  private imageObservationTimer?: ReturnType<typeof setTimeout>;
  private queuedCardImages = new Map<number, RestaurantDetails>();
  private activeCardImages = new Set<number>();
  private readonly maxConcurrentCardImages = 4;
  private cardImageQueueTimer?: ReturnType<typeof setTimeout>;
  // Reaching either end of the displayed batches loads the adjacent batch; there is no button.
  private pagingObserver?: IntersectionObserver;
  private readonly pagingMargin = 400;
  private pagingRetryAfter = 0;
  private scrollAnchor?: { id: number; top: number };
  // Map pins follow the list: two batches (60) stay well inside the measured pin budget.
  static readonly pinWindowPages = 2;
  private pinWindow: PinWindow | null = null;
  private listPositionTimer?: ReturnType<typeof setTimeout>;
  trackById(index: number, item: any): number {
    return item.id;
  }

  constructor(
    private modalService: NgbModal,
    private loaderService: LoaderService,
    private businessService: BusinessService,
    private route: Router,
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef,
    private tour: TourService
  ) {
    // Setup debounced load data
    this.loadDataSubject.pipe(
      takeUntil(this.destroy$),
      debounceTime(300)
    ).subscribe(() => {
      if (this.isCall) {
        this.loadData();
      }
    });
  }

  ngOnInit(): void {
    this.tourListing = this.tour.active;
    this.isFromMobileApp = hasNativeLoginBridge(window as any);
    const windowWidth = window.innerWidth;
    if (windowWidth < 768) {
      this.modalViewActive = true;
    } else {
      this.modalViewActive = false;
    }
    if (!this.tourListing && this.businessService.filterDataObject) {
      this.isFromDashboard = true;
    }
    if (this.businessFilter.origin_Lat == null || this.businessFilter.origin_long == null) {
      this.businessFilter.origin_Lat = '-0.7893';
      this.businessFilter.origin_long = '113.9213';
    }
    this.businessService.onOpenClosedBtnClick
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.showOpen = res.showOpen;
        this.showClose = res.showClose;
        this.scrollTotop();
        this.onCloseOpenResturant();
        this.loadDataSubject.next();
      });

    const closeOpenResturant = this.businessService.getCloseOpenResturant();
    this.showOpen = this.tourListing ? false : closeOpenResturant.showOpen;
    this.showClose = this.tourListing ? false : closeOpenResturant.showClose;
    const restored = this.restoreListingFromSessionState();
    if (!restored) {
      if (this.showClose || this.showOpen) {
        this.onCloseOpenResturant();
        this.loadDataSubject.next();
      } else if (this.isCall) {
        this.loadDataSubject.next();
      }
    }

  }

  private restoreListingFromSessionState(): boolean {
    if (this.tour.active || this.tourListing) return false;
    if (this.restoredFromSession) {
      return true;
    }
    if (this.businessService.filterDataObject && !this.businessService.hasListingReturnPending()) {
      return false;
    }
    const storedState = this.businessService.hydrateListingState();
    const storedFilterData = storedState?.filterDataObject;
    if (!storedState || !this.businessService.shouldRestoreListingState(storedState)) {
      return false;
    }

    const storedList = storedState.restaurantDetailsList;
    const storedMeta = storedState.listMeta;
    // Old responses were unpaged and cannot supply a valid continuation offset.
    if (storedMeta?.limit !== this.limit || storedMeta?.pagingVersion !== 1) return false;
    if (this.restaurantListType && storedMeta?.restaurantListType &&
        this.restaurantListType !== storedMeta.restaurantListType) return false;
    // An older saved Open Now list may have been fetched without its open-only filter.
    if (this.restaurantListType === RestaurantListType.OpenNow && storedFilterData?.filter?.isOpen !== true) return false;
    const hasStoredList = Array.isArray(storedList);
    const shouldRestoreEmpty = hasStoredList && storedList.length === 0 && typeof storedMeta?.totalRecords === 'number' && storedMeta.totalRecords === 0;
    if (!hasStoredList || (storedList.length === 0 && !shouldRestoreEmpty)) {
      return false;
    }

    this.restaurantDetailsList = storedList;
    const byId = new Map<number, RestaurantDetails>(storedList.map(item => [item.id, item]));
    this.visiblePages.clear();
    for (const page of storedMeta.pages || []) {
      this.visiblePages.set(page.page, page.ids.map(id => byId.get(id)).filter(Boolean));
    }
    this.hasLoadedPage = true;
    this.rebuildRestaurantCache(this.restaurantDetailsList);
    this.restaurantList = Array.isArray(storedState.restaurantList) ? storedState.restaurantList : storedList;
    this.resturantCoordinates = Array.isArray(storedState.resturantCoordinates)
      ? storedState.resturantCoordinates
      : this.buildCoordinatesFromList(this.restaurantDetailsList);

    const meta = storedMeta || {};
    if (typeof meta.currentPage === 'number') {
      this.currentPage = meta.currentPage;
    }
    if (typeof meta.totalPage === 'number') {
      this.totalPage = meta.totalPage;
    }
    if (typeof meta.totalRecords === 'number') {
      this.totalRecords = meta.totalRecords;
    }
    if (typeof meta.limit === 'number') {
      this.limit = meta.limit;
    }
    if (typeof meta.scrollTop === 'number') {
      this.pendingScrollTop = meta.scrollTop;
    }
    if (typeof meta.showOpen === 'boolean') {
      this.showOpen = meta.showOpen;
    } else if (typeof storedFilterData?.showOpen === 'boolean') {
      this.showOpen = storedFilterData.showOpen;
    }
    if (typeof meta.showClose === 'boolean') {
      this.showClose = meta.showClose;
    } else if (typeof storedFilterData?.showClose === 'boolean') {
      this.showClose = storedFilterData.showClose;
    }
    if (storedState.searchRestaurantList) {
      this.searchRestaurantList = storedState.searchRestaurantList;
    }

    if (Array.isArray(this.searchIds) && this.searchIds.length > 0) {
      this.applySearchIdsFilter();
    } else {
      this.onListLoad.emit(this.resturantCoordinates);
      this.businessService.updateRestaurantList(this.resturantCoordinates);
    }

    this.restoredFilterKey = this.buildFilterKey(storedFilterData?.filter, this.showOpen, this.showClose);
    this.lastListEpicenter = this.getFilterEpicenter(storedFilterData?.filter);
    this.skipNextCenterLoad = true;
    this.skipNextLoad = false;
    this.ignoreNextFilterReset = true;
    this.restoredFromSession = true;
    this.cdr.markForCheck();
    this.tryRestoreScrollPosition();
    return true;
  }

  private buildCoordinatesFromList(list: RestaurantDetails[]): any[] {
    return list
      .filter(x => x.coordinate?.lat && x.coordinate?.lng)
      .map((x) => ({
        index: x.index,
        id: x.id,
        lat: x.coordinate.lat,
        lng: x.coordinate.lng,
        restaurantId: x.id,
        listingIndex: x.index,
        absoluteIndex: x.index,
        distance: x.distance,
        zoomLevel: x.zoomLevel,
        name: x.restaurantName
      }));
  }

  private buildFilterKey(filter: BusinessFilter | any, showOpen?: boolean, showClose?: boolean): string {
    if (!filter) return '';
    const lat = filter.origin_Lat != null ? Number(filter.origin_Lat) : null;
    const lng = filter.origin_long != null ? Number(filter.origin_long) : null;
    const normalizeCoord = (value: number | null) =>
      typeof value === 'number' && !Number.isNaN(value) ? value.toFixed(6) : '';

    return JSON.stringify({
      search: filter.search ?? '',
      price: filter.FilterByPriceRange ?? '',
      categories: filter.FilterByCategories ?? '',
      meal: filter.FilterByMealType ?? '',
      lat: normalizeCoord(lat),
      lng: normalizeCoord(lng),
      showOpen: !!showOpen,
      showClose: !!showClose
    });
  }

  private setupIntersectionObserver() {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = +entry.target?.id;
          const restaurant = this.restaurantDetailsList[index];
          if (index >= 0 && restaurant) {
            if (entry.isIntersecting) {
              this.visibleImageIds.add(restaurant.id);
              restaurant.isVisible = true;
              this.queueCardImage(restaurant);
              this.cdr.markForCheck();
            } else {
              this.visibleImageIds.delete(restaurant.id);
            }
          }
        });
        this.trimCardImages();
      },
      // The mobile sheet can be taller than the screen. A viewport root avoids
      // treating every off-screen card inside that sheet as already visible.
      { root: null, rootMargin: '300px 0px', threshold: 0.01 }
    );
  }

  ngAfterViewInit() {
    this.setupIntersectionObserver();
    this.observeListItems();
    this.tryRestoreScrollPosition();
    this.restaurantListItems.changes
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.observeListItems();
      });
    if (typeof IntersectionObserver !== 'undefined') {
      // Viewport root, like the card observer: the mobile sheet can be taller than the screen.
      this.pagingObserver = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) this.checkPagingSentinels();
      }, { root: null, rootMargin: `${this.pagingMargin}px 0px` });
    }
    this.observePagingSentinels();
    this.pagingSentinels?.changes.pipe(takeUntil(this.destroy$)).subscribe(() => this.observePagingSentinels());
  }

  private observePagingSentinels() {
    if (!this.pagingObserver) return;
    this.pagingObserver.disconnect();
    this.pagingSentinels?.forEach(sentinel => this.pagingObserver.observe(sentinel.nativeElement));
  }

  /**
   * Loads the next batch when the list end is near, or the previous batch when the
   * list start is near. Called by the observer and again after every render, since
   * a sentinel that stays in range after a load does not produce a new observer entry.
   */
  private checkPagingSentinels() {
    if (this.destroyed || this.isLoading || Date.now() < this.pagingRetryAfter || !this.pagingSentinels) return;
    const inRange = (name: string) => this.pagingSentinels.some(sentinel => {
      const element = sentinel.nativeElement;
      if (element.dataset['paging'] !== name || !element.isConnected) return false;
      const rect = element.getBoundingClientRect();
      const container = this.listScrollContainer?.nativeElement?.getBoundingClientRect();
      const top = Math.max(0, container?.top ?? 0) - this.pagingMargin;
      const bottom = Math.min(window.innerHeight, container?.bottom ?? window.innerHeight) + this.pagingMargin;
      return rect.bottom >= top && rect.top <= bottom;
    });
    if (this.hasMoreBusinesses && inRange('next')) this.loadMoreBusinesses();
    else if (this.hasEarlierBusinesses && inRange('previous')) this.loadEarlierBusinesses();
  }

  /** Remembers the first card in view so adding or retiring a batch does not move the reader. */
  private captureScrollAnchor() {
    this.scrollAnchor = undefined;
    const container = this.listScrollContainer?.nativeElement;
    if (!container || !this.restaurantListItems) return;
    const top = Math.max(0, container.getBoundingClientRect().top);
    for (const element of this.listElements()) {
      const rect = element.getBoundingClientRect();
      const restaurant = this.restaurantDetailsList[Number(element.id)];
      if (restaurant && rect.bottom > top) {
        this.scrollAnchor = { id: restaurant.id, top: rect.top };
        return;
      }
    }
  }

  private restoreScrollAnchor() {
    const anchor = this.scrollAnchor;
    this.scrollAnchor = undefined;
    const container = this.listScrollContainer?.nativeElement;
    if (!anchor || !container || !this.restaurantListItems) return;
    const index = this.restaurantDetailsList.findIndex(item => item.id === anchor.id);
    const element = this.listElements().find(item => Number(item.id) === index);
    if (!element) return;
    const delta = element.getBoundingClientRect().top - anchor.top;
    if (Math.abs(delta) < 1) return;
    if (container.scrollHeight > container.clientHeight) container.scrollTop += delta;
    else window.scrollBy(0, delta);
  }

  /**
   * Throttled list-position work: the pin window and the end-of-list check. Runs
   * outside change detection, because both can change bound state.
   */
  onListScrollPosition() {
    if (this.destroyed || this.listPositionTimer != null) return;
    this.listPositionTimer = setTimeout(() => {
      this.listPositionTimer = undefined;
      this.updatePinWindow();
      this.checkPagingSentinels();
    }, 80);
  }

  /**
   * Shows pins only for the batches being read: the batch of the first card in view
   * and the next one. Scrolling past a batch retires its pins and shows the next set.
   */
  private updatePinWindow() {
    if (this.destroyed) return;
    let next: PinWindow | null = null;
    const indexed = !this.searchIds?.length && !this.businessFilter?.search?.trim() && this.restaurantDetailsList.length > 0;
    if (indexed) {
      const pages = RestaurantDetailSectionComponent.pinWindowPages;
      const lastIndex = Math.max(...this.restaurantDetailsList.map(item => item.index || 0));
      const firstIndex = Math.min(...this.restaurantDetailsList.map(item => item.index || 0));
      const topIndex = this.firstIndexInView() ?? firstIndex;
      const lastPage = Math.floor((lastIndex - 1) / this.limit);
      const firstPage = Math.floor((firstIndex - 1) / this.limit);
      const startPage = Math.max(firstPage, Math.min(Math.floor((topIndex - 1) / this.limit), lastPage - pages + 1));
      next = { start: startPage * this.limit + 1, end: (startPage + pages) * this.limit };
    }
    if (next?.start === this.pinWindow?.start && next?.end === this.pinWindow?.end) return;
    this.pinWindow = next;
    this.pinWindowChange.emit(next);
  }

  private listElements(): HTMLElement[] {
    const elements: HTMLElement[] = [];
    this.restaurantListItems?.forEach(item => elements.push(item.nativeElement));
    return elements;
  }

  private firstIndexInView(): number | null {
    const container = this.listScrollContainer?.nativeElement;
    if (!container || !this.restaurantListItems) return null;
    const top = Math.max(0, container.getBoundingClientRect().top);
    for (const element of this.listElements()) {
      if (element.getBoundingClientRect().bottom > top) {
        return this.restaurantDetailsList[Number(element.id)]?.index ?? null;
      }
    }
    return null;
  }

  ngOnChanges(changes: SimpleChanges) {
    // Location can resolve without any Maps event (including an SDK failure).
    // The readiness input must therefore start the first request itself.
    if (changes['isCall']?.currentValue && !changes['isCall'].firstChange) {
      this.loadDataSubject.next();
    }
    const currentSearchIdsLength = Array.isArray(this.searchIds) ? this.searchIds.length : 0;
    if (changes['searchIds']) {
      if (!changes['searchIds'].firstChange) {
        this.restoredFromSession = false;
      }
    }

    if (this.center && currentSearchIdsLength == 0 && changes['center'] && !changes['businessFilter']) {
      if (this.skipNextCenterLoad) {
        this.skipNextCenterLoad = false;
        return;
      }
      if (this.restoredFromSession) {
        this.restoredFromSession = false;
      }
      const centerPoint = this.getCenterPoint(this.center);
      const movementContext = this.getMovementContext(centerPoint);
      this.businessFilter.origin_Lat = this.center.lat;
      this.businessFilter.origin_long = this.center.lng;
      this.businessFilter.north = this.center.north;
      this.businessFilter.south = this.center.south;
      this.businessFilter.east = this.center.east;
      this.businessFilter.west = this.center.west;
      this.businessFilter.zoomLevel = this.center.zoom;
      this.businessFilter.enableZoomFallBack = this.center.enableZoomFallBack;
      this.currentPage = 0;
      this.requestedPage = undefined;
      this.hasLoadedPage = false;
      if (movementContext.isMeaningfulMove) {
        this.currentPage = 0;
        this.pendingEpicenterRefresh = true;
        this.lastListEpicenter = centerPoint;
        this.restaurantCache = this.pruneRestaurantCache(centerPoint, movementContext.hardRelevanceMeters);
        this.scrollTotop();
      } else if (!this.lastListEpicenter && centerPoint) {
        this.lastListEpicenter = centerPoint;
      }
      if (!movementContext.isMeaningfulMove && this.businessFilter.zoomLevel >= this.restaurantDetailsList[0]?.zoomLevel) {
        this.scrollTotop();
      }
      this.loadData();
    }
    else if (currentSearchIdsLength > 0) {
      if (this.restaurantDetailsList?.length > 0) {
        this.pendingSearchIdsFilter = false;
        this.applySearchIdsFilter();
      } else {
        this.pendingSearchIdsFilter = true;
        if (this.center) {
          this.businessFilter.origin_Lat = this.center.lat;
          this.businessFilter.origin_long = this.center.lng;
          this.businessFilter.north = this.center.north;
          this.businessFilter.south = this.center.south;
          this.businessFilter.east = this.center.east;
          this.businessFilter.west = this.center.west;
          this.businessFilter.zoomLevel = this.center.zoom;
          this.businessFilter.enableZoomFallBack = this.center.enableZoomFallBack;
        }
        if (this.isCall) {
          this.loadData();
        }
      }
    }
  }

  private applySearchIdsFilter() {
    this.restaurantList = this.restaurantDetailsList;
    // Cards and pins share one numbering, so a pin always scrolls to its own card.
    this.restaurantDetailsList = this.restaurantList.filter(x => this.searchIds.includes(x.id))
      .map((x, i) => ({ ...x, index: i + 1 }));

    const filteredCoordinates = this.restaurantDetailsList
      .filter(x => x.coordinate.lat && x.coordinate.lng)
      .map(x => ({
        index: x.index,
        id: x.id,
        lat: x.coordinate.lat,
        lng: x.coordinate.lng,
        restaurantId: x.id,
        listingIndex: x.index,
        absoluteIndex: x.index,
        distance: x.distance,
        zoomLevel: x.zoomLevel,
        name: x.restaurantName
      }));

    this.resturantCoordinates = filteredCoordinates;
    this.onListLoad.emit(this.resturantCoordinates);
    this.businessService.updateRestaurantList(this.resturantCoordinates);
    this.persistListingState();
  }

  private observeListItems() {
    if (this.destroyed) return;
    this.pruneCardImages(this.restaurantDetailsList);
    if (this.intersectionObserver && this.restaurantListItems) {
      this.intersectionObserver.disconnect();
      this.visibleImageIds.clear();
      this.restaurantListItems.forEach(item => {
        const restaurant = this.restaurantDetailsList[Number(item.nativeElement.id)];
        if (restaurant) {
          const rect = item.nativeElement.getBoundingClientRect();
          if (rect.bottom >= -300 && rect.top <= window.innerHeight + 300) this.visibleImageIds.add(restaurant.id);
          this.intersectionObserver.observe(item.nativeElement);
        }
      });
    }
    // Load all photos in the fetched result set. Visibility only determines priority.
    this.restaurantDetailsList.forEach(restaurant => this.queueCardImage(restaurant));
    this.tryRestoreScrollPosition();
    this.onListScrollPosition();
  }

  private queueCardImage(restaurant: RestaurantDetails) {
    if (this.destroyed || this.cardImages.get(restaurant.id)?.key === this.cardImageKey(restaurant)) return;
    this.queuedCardImages.set(restaurant.id, restaurant);
    this.scheduleCardImages();
  }

  private scheduleCardImages() {
    if (this.destroyed || !this.queuedCardImages.size || this.cardImageQueueTimer != null) return;
    this.cardImageQueueTimer = setTimeout(() => {
      this.cardImageQueueTimer = undefined;
      while (!this.destroyed && this.activeCardImages.size < this.maxConcurrentCardImages && this.queuedCardImages.size) {
        const queued = [...this.queuedCardImages.values()];
        const restaurant = queued.find(item => this.visibleImageIds.has(item.id)) || queued[0];
        this.queuedCardImages.delete(restaurant.id);
        if (this.cardImages.get(restaurant.id)?.key === this.cardImageKey(restaurant)) continue;
        this.loadCardImage(restaurant);
      }
    });
  }

  loadData() {
    if (this.destroyed || !this.isCall) return;
    if (this.skipNextLoad) {
      this.skipNextLoad = false;
      return;
    }
    const currentRequestId = ++this.requestId;
    this.listingRequest?.unsubscribe();
    this.isLoading = true;
    this.loadError = '';
    this.cdr.markForCheck();

    this.businessFilter.offset = this.requestedPage ?? this.currentPage;
    this.businessFilter.sortingType = this.restaurantListType;
    this.businessFilter.limit = this.limit;
    this.businessFilter.timeZoneOffset = new Date().getTimezoneOffset();

    if (this.restaurantListType === 2) {
      // Open Now is a filter, even when a closed-only pill was used on another tab.
      this.businessFilter.isOpen = true;
    } else if (this.showOpen || this.showClose) {
      if (this.showOpen)
        this.businessFilter.isOpen = true;
      else
        this.businessFilter.isOpen = false;
    }
    else {
      this.businessFilter.isOpen = null;
    }
    const requestFilter = { ...this.businessFilter };
    // The client checks every viewport. Do not let the stored procedure widen it.
    if (listingViewport(requestFilter) && !requestFilter.search?.trim()) requestFilter.enableZoomFallBack = false;
    const requestKey = listingQueryKey(requestFilter);
    this.listingRequest = quietRead(() => this.tourListing
      // A demonstration uses the verified branch directly, so area pagination,
      // neighboring restaurants and open/closed filters cannot change its target.
      ? this.businessService.getBusinessById(this.tour.restaurantId, new HttpContext().set(QUIET_HTTP_ERRORS, true)).pipe(map(business => {
          if (!this.tour.matchesRestaurant(business?.branchId, business?.name)) {
            throw new Error('The guided restaurant is unavailable.');
          }
          return { records: [business], total: 1 };
        }))
      : this.businessService.getBusinessList(requestFilter, new HttpContext().set(QUIET_HTTP_ERRORS, true)))
      .pipe(takeUntil(this.destroy$), finalize(() => {
        if (currentRequestId === this.requestId) {
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      }))
      .subscribe({
        next: (response) => {
          if (response && Array.isArray(response.records) && currentRequestId === this.requestId
              && requestKey === listingQueryKey(this.businessFilter)) {
            const page = requestFilter.offset;
            const adjacentBatch = this.requestedPage != null;
            if (adjacentBatch) this.captureScrollAnchor();
            if (this.requestedPage == null) {
              this.visiblePages.clear();
            }
            this.requestedPage = undefined;
            this.currentPage = page;
            this.hasLoadedPage = true;
            if (page === 0) {
              this.restaurantDetailsList = [];
              this.restaurantCache.clear();
              this.resturantCoordinates = [];
              this.restaurantList = [];
            }

            this.searchRestaurantList = null;
            if (this.businessFilter?.search != null && this.businessFilter?.search != undefined && this.businessFilter?.search != '') {
              if (this.isFromDashboard) {
                this.processDirectSearchResponse(response);
              } else {
                this.searchRestaurantList = response;
                this.persistListingState();
              }
            } else {
              this.processBusinessResponse(response);
              if (this.pendingSearchIdsFilter || (Array.isArray(this.searchIds) && this.searchIds.length > 0)) {
                this.pendingSearchIdsFilter = false;
                this.applySearchIdsFilter();
              }
            }
            if (this.businessFilter.search) {
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }
            this.cdr.markForCheck();
            if (adjacentBatch) {
              // Render now so the batch added above (or retired above) can be offset in the same frame.
              this.cdr.detectChanges();
              this.restoreScrollAnchor();
            }
            this.isFromDashboard = false;
            // Only an actual response may advance auto-zoom; marker updates cannot.
            this.loadCompleted.emit({ filter: requestFilter, coordinates: [...this.resturantCoordinates], total: response.total });
            if (this.tourListing && this.tour.step === 'list') {
              if (this.restaurantDetailsList.some(item => Number(item.id) === this.tour.restaurantId)) {
                this.tour.notify('listing-ready');
              } else if (this.currentPage + 1 >= this.totalPage) {
                this.tour.notify('restaurant-unavailable');
              }
            }
          }
        },
        error: () => {
          if (currentRequestId === this.requestId) {
            // A failed adjacent batch is retried by scrolling again, but never in a tight loop.
            this.pagingRetryAfter = Date.now() + 5000;
            if (this.tourListing && this.tour.step === 'list') this.tour.notify('restaurant-unavailable');
            this.loadError = 'Restaurants could not load. Please try again.';
            this.loadFailed.emit();
            this.cdr.markForCheck();
          }
        }
      });
  }

  private processBusinessResponse(response: any) {
    this.totalRecords = response.total;
    this.totalPage = Math.ceil(response.total / this.businessFilter.limit);

    const newRestaurants = response.records.map((business) =>
      this.mapBusinessToRestaurant(business, 0)
    );
    const page = this.currentPage;
    const movingBack = this.visiblePages.size > 0 && page < this.firstVisiblePage;
    this.visiblePages.set(page, newRestaurants);
    while (this.visiblePages.size > this.maxVisiblePages) {
      const pages = [...this.visiblePages.keys()];
      this.visiblePages.delete(movingBack ? Math.max(...pages) : Math.min(...pages));
    }
    this.currentPage = Math.max(...this.visiblePages.keys());
    this.restaurantCache.clear();
    [...this.visiblePages.entries()].sort((a, b) => a[0] - b[0]).forEach(([, restaurants]) =>
      restaurants.forEach(restaurant => this.restaurantCache.set(restaurant.id, restaurant)));
    if (this.pendingEpicenterRefresh && !listingViewport(this.businessFilter)) {
      const epicenter = this.getFilterEpicenter(this.businessFilter);
      const hardRelevanceMeters = this.getRelevanceThresholdsFromFilter().hard;
      this.restaurantCache = this.pruneRestaurantCache(epicenter, hardRelevanceMeters);
      this.pendingEpicenterRefresh = false;
    }
    this.pendingEpicenterRefresh = false;
    this.restaurantDetailsList = this.getRankedRestaurantsForCurrentContext();
    this.restaurantList = this.restaurantDetailsList;
    const newCoordinates = this.restaurantDetailsList
      .filter(x => x.coordinate.lat != null && x.coordinate.lng != null)
      .map(x => ({
        index: x.index,
        id: x.id,
        lat: x.coordinate.lat,
        lng: x.coordinate.lng,
        restaurantId: x.id,
        listingIndex: x.index,  // This is now the absolute index
        absoluteIndex: x.index, // Adding this for clarity
        distance: x.distance,
        zoomLevel: x.zoomLevel,
        name: x.restaurantName
      }));

    this.resturantCoordinates = newCoordinates;
    this.onListLoad.emit(this.resturantCoordinates);

    // Update map markers
    this.businessService.updateRestaurantList(this.resturantCoordinates);
    this.persistListingState();
  }

  private processDirectSearchResponse(response: any) {
    this.totalRecords = response.total;
    this.totalPage = Math.ceil(response.total / this.businessFilter.limit);

    const restaurants = response.records.map((business, index) => {
      const restaurant = this.mapBusinessToRestaurant(business, index + 1);
      restaurant.zoomLevel = this.businessFilter.zoomLevel;
      restaurant.isVisible = true;
      return restaurant;
    });

    this.restaurantDetailsList = restaurants;
    this.restaurantList = restaurants;
    this.pruneCardImages(restaurants);
    this.resturantCoordinates = this.buildCoordinatesFromList(restaurants);
    this.onListLoad.emit(this.resturantCoordinates);
    this.businessService.updateRestaurantList(this.resturantCoordinates);
    this.persistListingState();
  }

  private rebuildRestaurantCache(restaurants: RestaurantDetails[]) {
    this.restaurantCache.clear();
    restaurants.forEach(restaurant => this.restaurantCache.set(restaurant.id, restaurant));
    this.pruneCardImages(restaurants);
  }

  private pruneCardImages(restaurants: RestaurantDetails[]) {
    const retained = new Set(restaurants.map(restaurant => restaurant.id));
    this.queuedCardImages.forEach((_, id) => { if (!retained.has(id)) this.queuedCardImages.delete(id); });
    this.cardImages.forEach((image, id) => {
      if (!retained.has(id)) {
        this.visibleImageIds.delete(id);
        if (image.subscription && !image.subscription.closed) {
          image.subscription.unsubscribe();
          this.cardImages.delete(id);
        }
      }
    });
    this.trimCardImages();
  }

  private trimCardImages() {
    // Data URLs are UTF-16 strings. Also bound entries that only contain HTTP URLs.
    let bytes = [...this.cardImages.values()].reduce((sum, item) => sum + item.url.length * 2, 0);
    const candidates = [...this.cardImages.keys()].filter(id => !this.visibleImageIds.has(id));
    for (const id of candidates) {
      if (this.cardImages.size <= this.maxCardImages && bytes <= this.maxCardImageBytes) break;
      const image = this.cardImages.get(id);
      bytes -= image.url.length * 2;
      image.subscription?.unsubscribe();
      this.cardImages.delete(id);
    }
    // Visible cards are pinned while being displayed, then become evictable on exit.
    this.cdr.markForCheck();
  }

  private getRankedRestaurantsForCurrentContext(): RestaurantDetails[] {
    const epicenter = this.getFilterEpicenter(this.businessFilter);
    const hardRelevanceMeters = this.getRelevanceThresholdsFromFilter().hard;
    const restaurants = Array.from(this.restaurantCache.values())
      .filter(restaurant => listingViewport(this.businessFilter)
        ? isInListingViewport(restaurant.coordinate, this.businessFilter)
        : this.isRestaurantRelevantToEpicenter(restaurant, epicenter, hardRelevanceMeters))
      .map(restaurant => ({
        ...restaurant,
        distance: epicenter ? this.getDistanceMeters(epicenter, this.getRestaurantPoint(restaurant)) : restaurant.distance,
        zoomLevel: this.businessFilter.zoomLevel,
        isVisible: true
      }))
      .sort((a, b) => (a.distance ?? Number.MAX_SAFE_INTEGER) - (b.distance ?? Number.MAX_SAFE_INTEGER) || a.id - b.id)
      .slice(0, this.maxCachedRestaurants)
      .map((restaurant, index) => ({
        ...restaurant,
        index: this.firstVisiblePage * this.limit + index + 1
      }));

    this.rebuildRestaurantCache(restaurants);
    return restaurants;
  }

  private getMovementContext(nextEpicenter: { lat: number; lng: number } | null) {
    const thresholds = this.getRelevanceThresholdsFromCenter(this.center);
    if (!nextEpicenter || !this.lastListEpicenter) {
      return { isMeaningfulMove: false, distanceMeters: 0, hardRelevanceMeters: thresholds.hard };
    }
    const distanceMeters = this.getDistanceMeters(this.lastListEpicenter, nextEpicenter);
    return {
      isMeaningfulMove: distanceMeters > thresholds.refresh,
      distanceMeters,
      hardRelevanceMeters: thresholds.hard
    };
  }

  private getRelevanceThresholdsFromFilter() {
    return this.getRelevanceThresholdsFromCenter(this.businessFilter);
  }

  private getRelevanceThresholdsFromCenter(center: any) {
    const dims = this.getViewportDimsMeters(center);
    const shorterRadius = Math.min(dims.width, dims.height) / 2;
    const refresh = this.clamp(shorterRadius * 0.30, 120, 2500);
    const soft = this.clamp(shorterRadius, 500, 6000);
    const hard = this.clamp(shorterRadius * 1.5, 1000, 10000);
    return { refresh, soft, hard };
  }

  private getViewportDimsMeters(center: any) {
    const north = Number(center?.north);
    const south = Number(center?.south);
    const east = Number(center?.east);
    const west = Number(center?.west);
    if ([north, south, east, west].some(value => Number.isNaN(value))) {
      return { width: 0, height: 0 };
    }
    const midLat = (north + south) / 2;
    return {
      width: this.getDistanceMeters({ lat: midLat, lng: west }, { lat: midLat, lng: east }),
      height: this.getDistanceMeters({ lat: south, lng: west }, { lat: north, lng: west })
    };
  }

  private pruneRestaurantCache(epicenter: { lat: number; lng: number } | null, hardRelevanceMeters: number) {
    if (!epicenter) {
      return this.restaurantCache;
    }
    const nextCache = new Map<number, RestaurantDetails>();
    Array.from(this.restaurantCache.values())
      .filter(restaurant => this.isRestaurantRelevantToEpicenter(restaurant, epicenter, hardRelevanceMeters))
      .sort((a, b) => this.getDistanceMeters(epicenter, this.getRestaurantPoint(a)) - this.getDistanceMeters(epicenter, this.getRestaurantPoint(b)))
      .slice(0, this.maxCachedRestaurants)
      .forEach(restaurant => nextCache.set(restaurant.id, restaurant));
    return nextCache;
  }

  private isRestaurantRelevantToEpicenter(restaurant: RestaurantDetails, epicenter: { lat: number; lng: number } | null, hardRelevanceMeters: number) {
    if (!epicenter) {
      return true;
    }
    const point = this.getRestaurantPoint(restaurant);
    if (!point) {
      return false;
    }
    return this.getDistanceMeters(epicenter, point) <= hardRelevanceMeters;
  }

  private getFilterEpicenter(filter: any): { lat: number; lng: number } | null {
    return this.getCenterPoint({
      lat: filter?.origin_Lat,
      lng: filter?.origin_long
    });
  }

  private getCenterPoint(center: any): { lat: number; lng: number } | null {
    const lat = Number(center?.lat);
    const lng = Number(center?.lng);
    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      return null;
    }
    return { lat, lng };
  }

  private getRestaurantPoint(restaurant: RestaurantDetails): { lat: number; lng: number } | null {
    const lat = Number(restaurant?.coordinate?.lat);
    const lng = Number(restaurant?.coordinate?.lng);
    if (Number.isNaN(lat) || Number.isNaN(lng)) {
      return null;
    }
    return { lat, lng };
  }

  private getDistanceMeters(from: { lat: number; lng: number }, to: { lat: number; lng: number } | null): number {
    if (!to) {
      return Number.MAX_SAFE_INTEGER;
    }
    const toRadians = (degrees: number) => degrees * Math.PI / 180;
    const earthRadiusMeters = 6371000;
    const deltaLat = toRadians(to.lat - from.lat);
    const deltaLng = toRadians(to.lng - from.lng);
    const lat1 = toRadians(from.lat);
    const lat2 = toRadians(to.lat);
    const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
      Math.cos(lat1) * Math.cos(lat2) *
      Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return earthRadiusMeters * c;
  }

  private clamp(value: number, min: number, max: number) {
    return Math.max(min, Math.min(max, value));
  }

  private persistListingState() {
    if (this.tour.active || this.tourListing) return;
    this.businessService.saveListingState({
      restaurantDetailsList: this.restaurantDetailsList,
      restaurantList: this.restaurantList,
      resturantCoordinates: this.resturantCoordinates,
      searchRestaurantList: this.searchRestaurantList,
      listMeta: {
        pagingVersion: 1,
        pages: [...this.visiblePages.entries()].map(([page, restaurants]) => ({ page, ids: restaurants.map(item => item.id) })),
        currentPage: this.currentPage,
        totalPage: this.totalPage,
        totalRecords: this.totalRecords,
        limit: this.limit,
        restaurantListType: this.restaurantListType,
        showOpen: this.showOpen,
        showClose: this.showClose,
        scrollTop: this.listScrollContainer?.nativeElement?.scrollTop ?? 0
      }
    });
    // TrackBy can retain every card node while its image metadata changes.
    // Recheck after Angular updates those views as well as on QueryList changes.
    clearTimeout(this.imageObservationTimer);
    this.imageObservationTimer = setTimeout(() => this.observeListItems());
  }

  private tryRestoreScrollPosition() {
    if (this.pendingScrollTop == null || !this.listScrollContainer?.nativeElement) {
      return;
    }

    const scrollTop = this.pendingScrollTop;
    requestAnimationFrame(() => {
      if (!this.listScrollContainer?.nativeElement) {
        return;
      }
      this.listScrollContainer.nativeElement.scrollTop = scrollTop;
      this.pendingScrollTop = null;
    });
  }

  private mapBusinessToRestaurant(business: any, index: number): RestaurantDetails {
    const photos = (business.membershipBusinessImagesList || []).filter(image => !image.isDeleted && image.imagePath?.trim());
    const cover = photos.find(image => Number(image.imageType) === 1) || photos[0];
    const hours = (business.membershipAppCompanyBusinessHourList || []).map(hour => ({
      ...hour,
      from: hour.from?.length === 7 ? '0' + hour.from : (hour.from || ''),
      to: hour.to?.length === 7 ? '0' + hour.to : (hour.to || '')
    }));

    const restaurant = {
      index: index,
      id: business.branchId,
      photo: cover?.imagePath,
      restaurantName: business.name,
      typeName: business.typeName,
      status: !hours.length || typeof business.isOpen !== 'boolean' ? '' : business.isOpen ? 'Open Now' : 'Closed Now',
      cuisineName: business.cuisineName,
      priceRange: business.priceRange,
      cityName: business.cityName,
      address: business.address,
      rank: null,
      rating: null,
      openWeeksHours: businessHours.groupBySameTiming(hours),
      membershipAppCompanyBusinessHourList: hours,
      coordinate: {
        lat: business.lat,
        lng: business.lng
      },
      showMore: false,
      tags: this.generateTags(business),
      toolTipHours: hours.filter(hour =>
        hour.from !== "" || hour.to !== ""
      ) || [],
      distance: business.distance,
      memberShipImageConfigurationList: cover?.memberShipImageConfigurationViews?.find((y) => y.imageType == 2),
      zoomLevel: business.zoomLevel,
      directionsUrl: directionsUrl(business.lat, business.lng, business.address)
    } as RestaurantDetails;

    return restaurant;
  }

  private generateTags(business: any): string[] {
    const tags = [];
    if (business.typeName) tags.push(business.typeName);
    if (business.cuisineName) tags.push(business.cuisineName);
    if (business.priceRange) tags.push(business.priceRange);
    if (business.rank) tags.push(business.rank);
    return tags;
  }

  // One batch per arrival at a list end; scrolling never fetches the whole viewport.
  loadMoreBusinesses() {
    if (this.isLoading || !this.hasMoreBusinesses) return;
    // Explicit, not reused: a failed batch at the other end must not be requested instead.
    this.requestedPage = this.currentPage + 1;
    this.loadData();
  }

  loadEarlierBusinesses() {
    if (this.isLoading || !this.hasEarlierBusinesses) return;
    this.requestedPage = this.firstVisiblePage - 1;
    this.loadData();
  }

  ngOnDestroy() {
    this.destroyed = true;
    this.persistListingState();
    clearTimeout(this.imageObservationTimer);
    clearTimeout(this.cardImageQueueTimer);
    this.pagingObserver?.disconnect();
    clearTimeout(this.listPositionTimer);
    this.queuedCardImages.clear();
    this.activeCardImages.clear();
    this.cardImages.forEach(image => image.subscription?.unsubscribe());
    this.cardImages.clear();
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  onCloseOpenResturant() {
    ++this.requestId;
    this.listingRequest?.unsubscribe();
    this.isLoading = false;
    this.currentPage = 0;
    this.requestedPage = undefined;
    this.hasLoadedPage = false;
  }

  scrollTotop() {
    let myDiv = document.querySelector('.restro-details-wrap');
    if (myDiv != null) {
      myDiv.scrollTop = 0;
    }
  }

  getDayName(day: number): string {
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return dayNames[day];
  }

  /**
   * Reveals the hidden tag chips. Idempotent on purpose: desktop reaches it via
   * mouseover and touch/keyboard via click, and a tap fires both, so a toggle
   * would open and immediately close. The click must not bubble to the card,
   * which would open the preview modal.
   */
  showMoreTags(item, event?: Event) {
    event?.stopPropagation();
    item.showMore = true;
  }

  get allChipsVisible() {
    return !this.item.showMore;
  }

  // Modal Code

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    const windowWidth = window.innerWidth;
    if (windowWidth < 768) {
      this.modalViewActive = true;
    } else {
      this.modalViewActive = false;
      this.closeRestroContentModal();
    }
  }

  openRestroContentModal(
    restroContent: TemplateRef<any>,
    item: RestaurantDetails
  ) {
    if (this.tour.beginRestaurant(item.id, item.restaurantName)) return;
    if (!this.tourListing) localStorage.setItem("selectedBranchId", item?.id.toString());
    this.persistListingState();
    if (this.modalViewActive) {
      this.item = item;
      this.modalRef = this.modalService.open(restroContent, {
        windowClass: 'project-modal restro-details-modal',
        centered: true,
        backdropClass: 'light-backdrop',
      });
    }
    else {
      if (!this.tourListing) this.businessService.markListingReturnPending();
      this.route.navigate(['business-detail', item.id]);
    }
  }

  isTourRestaurant(item: RestaurantDetails): boolean {
    return this.tour.active && this.tour.step === 'list' && this.tour.matchesRestaurant(item.id, item.restaurantName);
  }

  onTourRestaurantKey(event: KeyboardEvent, item: RestaurantDetails, content: TemplateRef<any>) {
    if (event.target !== event.currentTarget || !this.isTourRestaurant(item)
      || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    event.stopPropagation();
    this.openRestroContentModal(content, item);
  }

  closeRestroContentModal() {
    if (this.modalRef) {
      this.modalRef.close();
      this.modalRef = null;
    }
    if (!this.tourListing) this.businessService.clearListingReturnPending();
  }

  onRestroScroll() {
    const div = this.elementRef.nativeElement.querySelector(
      '.restro-details-wrap'
    );
    this.renderer.addClass(div, 'scroll-active');
    setTimeout(() => {
      this.renderer.removeClass(div, 'scroll-active');
    }, 1500);
  }

  getCoordinates($event) {
    this.businessService.emitLocations($event);
  }

  resetMarkerIcon($event) {
    this.businessService.resetIcon($event);
  }

  hideSearchBar() {
    this.businessService.onScrollChange.emit();
  }

  private cardImageKey(restaurant: RestaurantDetails): string {
    const crop = restaurant.memberShipImageConfigurationList;
    return JSON.stringify([restaurant.photo, restaurantImageUrl(restaurant.photo, 'card'),
      crop?.x, crop?.y, crop?.width, crop?.height]);
  }

  @HostListener('window:online')
  @HostListener('window:focus')
  recoverListing(): void {
    if (this.destroyed || this.isLoading || !this.loadError || Date.now() < this.recoveryAfter) return;
    this.recoveryAfter = Date.now() + 30000;
    this.loadData();
  }

  cardImagePending(restaurant: RestaurantDetails): boolean {
    if (!restaurant.photo) return false;
    const image = this.cardImages.get(restaurant.id);
    return image?.key !== this.cardImageKey(restaurant) || image.pending;
  }

  cardImageUrl(restaurant: RestaurantDetails): string {
    const image = this.cardImages.get(restaurant.id);
    return image?.key === this.cardImageKey(restaurant) ? image.url : restaurant.photo ? '' : this.imagePlaceholder;
  }

  private loadCardImage(restaurant: RestaurantDetails) {
    const key = this.cardImageKey(restaurant);
    const previous = this.cardImages.get(restaurant.id);
    if (previous?.key === key) {
      this.cardImages.delete(restaurant.id);
      this.cardImages.set(restaurant.id, previous);
      return;
    }
    previous?.subscription?.unsubscribe();
    const image: CardImageState = { key, url: restaurant.photo ? '' : this.imagePlaceholder, stage: 'placeholder', pending: !!restaurant.photo };
    this.cardImages.set(restaurant.id, image);
    if (!restaurant.photo) { this.activeCardImages.delete(restaurant.id); this.trimCardImages(); return; }
    const profile = restaurantImageUrl(restaurant.photo, 'card');
    if (profile && profile !== restaurant.photo) {
      // The server already applied the owner's card crop. Never crop it twice.
      image.url = profile;
      image.stage = 'profile';
      image.pending = false;
      this.activeCardImages.delete(restaurant.id);
    } else {
      this.loadOriginalCardImage(restaurant, image);
    }
    this.trimCardImages();
  }

  private loadOriginalCardImage(restaurant: RestaurantDetails, image: CardImageState) {
    this.activeCardImages.add(restaurant.id);
    image.stage = 'original';
    const crop = restaurant.memberShipImageConfigurationList;
    const validCrop = crop && [crop.x, crop.y, crop.width, crop.height].every(Number.isFinite)
      && crop.x >= 0 && crop.y >= 0 && crop.width > 0 && crop.height > 0;
    image.url = '';
    image.pending = true;
    const prepare = validCrop ? this.cropImage(restaurant.photo, crop.x, crop.y, crop.width, crop.height)
      : this.preloadCardImage(restaurant.photo);
    image.subscription = prepare
      .pipe(timeout(10000), takeUntil(this.destroy$), finalize(() => {
        this.activeCardImages.delete(restaurant.id);
        this.scheduleCardImages();
      }))
      .subscribe({
        next: url => {
          if (this.cardImages.get(restaurant.id) !== image) return;
          image.url = url;
          image.pending = false;
          this.trimCardImages();
          this.cdr.markForCheck();
        },
        error: () => {
          if (this.cardImages.get(restaurant.id) !== image) return;
          // If crop preparation failed, let the image loader retry the original.
          // An existing photo must not be presented as a business with no photo.
          image.url = restaurant.photo;
          image.pending = false;
          image.stage = 'original';
          this.trimCardImages();
          this.cdr.markForCheck();
        }
      });
  }

  private preloadCardImage(source: string): Observable<string> {
    return new Observable(observer => {
      const image = new Image();
      image.onload = () => { observer.next(source); observer.complete(); };
      image.onerror = () => observer.error(new Error('Restaurant image unavailable.'));
      image.src = source;
      return () => { image.onload = image.onerror = null; image.removeAttribute('src'); };
    });
  }

  onCardImageError(restaurant: RestaurantDetails, event?: Event) {
    const image = this.cardImages.get(restaurant.id);
    if (!image || image.key !== this.cardImageKey(restaurant) || image.url === this.imagePlaceholder) return;
    const element = event?.target as HTMLImageElement | undefined;
    if (element && element.getAttribute('src') !== image.url) return;
    if (image.stage === 'profile') {
      this.loadOriginalCardImage(restaurant, image);
    }
    this.cdr.markForCheck();
  }

  cropImage(imageSrc: string, x: number, y: number, width: number, height: number): Observable<string> {
    return new Observable(observer => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        observer.error(new Error('Unable to prepare the restaurant image.'));
        return undefined;
      }

      const img = new Image();
      if (!imageSrc) {
        observer.error(new Error('Missing restaurant image.'));
        return undefined;
      }
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        if (observer.closed) return;
        try {
          const scale = Math.min(640 / width, 640 / height, 1);
          canvas.width = Math.max(1, Math.round(width * scale));
          canvas.height = Math.max(1, Math.round(height * scale));
          ctx.drawImage(img, x, y, width, height, 0, 0, canvas.width, canvas.height);
          observer.next(canvas.toDataURL('image/jpeg', 0.82));
          observer.complete();
        } catch (error) { observer.error(error); }
      };
      img.onerror = () => observer.error(new Error('Restaurant image unavailable.'));
      img.src = imageSrc;
      return () => {
        img.onload = null;
        img.onerror = null;
        img.removeAttribute('src');
      };
    });
  }

  onExpandList() {
    if (this.isLoading) return;
    // A failed request has not established an empty area. Retry it before widening again.
    if (this.loadError) { this.loadData(); return; }
    this.businessService.expandList(true);
  }

  onExpandSearch() {
    this.processDirectSearchResponse(this.searchRestaurantList);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
}
